// src/types/proyecto.ts

// Enums
export type ProjectStatus =
  | 'PLANEACION'
  | 'EN_PROGRESO'
  | 'EN_PAUSA'
  | 'COMPLETADO'
  | 'CANCELADO';

export type SprintStatus =
  | 'PLANIFICADO'
  | 'ACTIVO'
  | 'COMPLETADO'
  | 'CANCELADO';

export type RolProyecto =
  | 'RESPONSABLE'
  | 'COLABORADOR'
  | 'OBSERVADOR';

// Labels para UI
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  PLANEACION: 'Planeación',
  EN_PROGRESO: 'En Progreso',
  EN_PAUSA: 'En Pausa',
  COMPLETADO: 'Completado',
  CANCELADO: 'Cancelado',
};

export const PROJECT_STATUS_COLORS: Record<ProjectStatus, string> = {
  PLANEACION: 'bg-gray-100 text-gray-800',
  EN_PROGRESO: 'bg-blue-100 text-blue-800',
  EN_PAUSA: 'bg-yellow-100 text-yellow-800',
  COMPLETADO: 'bg-green-100 text-green-800',
  CANCELADO: 'bg-red-100 text-red-800',
};

export const SPRINT_STATUS_LABELS: Record<SprintStatus, string> = {
  PLANIFICADO: 'Planificado',
  ACTIVO: 'Activo',
  COMPLETADO: 'Completado',
  CANCELADO: 'Cancelado',
};

export const SPRINT_STATUS_COLORS: Record<SprintStatus, string> = {
  PLANIFICADO: 'bg-gray-100 text-gray-800',
  ACTIVO: 'bg-blue-100 text-blue-800',
  COMPLETADO: 'bg-green-100 text-green-800',
  CANCELADO: 'bg-red-100 text-red-800',
};

export const ROL_PROYECTO_LABELS: Record<RolProyecto, string> = {
  RESPONSABLE: 'Responsable',
  COLABORADOR: 'Colaborador',
  OBSERVADOR: 'Observador',
};

// Interfaces base
export interface UsuarioBasico {
  id: string;
  nombre: string;
  email?: string;
}

export interface ClienteBasico {
  id: string;
  nombre: string;
  codigo?: string;
}

// Proyecto
export interface Proyecto {
  id: string;
  codigo: string;
  nombre: string;
  descripcion?: string;
  cliente_id?: string;
  cliente?: ClienteBasico;
  ticket_origen_id?: string;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  fecha_inicio_real?: string;
  fecha_fin_real?: string;
  duracion_sprint_dias: number;
  estado: ProjectStatus;
  horas_estimadas?: number;
  horas_registradas: number;
  responsable_id?: string;
  responsable?: UsuarioBasico;
  fases_count: number;
  sprints_count: number;
  miembros_count: number;
  actividades_count: number;
  progreso_porcentaje: number;
  activo: boolean;
  created_at: string;
  updated_at?: string;
}

export interface ProyectoListItem {
  id: string;
  codigo: string;
  nombre: string;
  estado: ProjectStatus;
  cliente_nombre?: string;
  responsable_nombre?: string;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  horas_estimadas?: number;
  horas_registradas: number;
  progreso_porcentaje: number;
}

export interface ProyectoCreate {
  codigo: string;
  nombre: string;
  descripcion?: string;
  cliente_id?: string;
  ticket_origen_id?: string;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  duracion_sprint_dias?: number;
  horas_estimadas?: number;
  responsable_id?: string;
}

export interface ProyectoUpdate {
  nombre?: string;
  descripcion?: string;
  cliente_id?: string;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  fecha_inicio_real?: string;
  fecha_fin_real?: string;
  duracion_sprint_dias?: number;
  estado?: ProjectStatus;
  horas_estimadas?: number;
  responsable_id?: string;
}

export interface ProyectoFiltros {
  estado?: ProjectStatus;
  cliente_id?: string;
  responsable_id?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  solo_activos?: boolean;
}

// Fase
export interface ProyectoFase {
  id: string;
  proyecto_id: string;
  nombre: string;
  descripcion?: string;
  orden: number;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  fecha_inicio_real?: string;
  fecha_fin_real?: string;
  estado: string;
  es_hito: boolean;
  horas_estimadas?: number;
  horas_registradas: number;
  actividades_count: number;
  actividades_completadas_count: number;
  created_at: string;
}

export interface ProyectoFaseCreate {
  nombre: string;
  descripcion?: string;
  orden?: number;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  es_hito?: boolean;
  horas_estimadas?: number;
}

export interface ProyectoFaseUpdate {
  nombre?: string;
  descripcion?: string;
  orden?: number;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  fecha_inicio_real?: string;
  fecha_fin_real?: string;
  estado?: string;
  es_hito?: boolean;
  horas_estimadas?: number;
}

// Sprint
export interface ProyectoSprint {
  id: string;
  proyecto_id: string;
  nombre: string;
  numero: number;
  fecha_inicio: string;
  fecha_fin: string;
  objetivo?: string;
  estado: SprintStatus;
  capacidad_horas?: number;
  horas_planeadas: number;
  horas_registradas: number;
  actividades_count: number;
  actividades_completadas_count: number;
  created_at: string;
}

export interface ProyectoSprintCreate {
  nombre: string;
  numero: number;
  fecha_inicio: string;
  fecha_fin: string;
  objetivo?: string;
  capacidad_horas?: number;
}

export interface ProyectoSprintUpdate {
  nombre?: string;
  fecha_inicio?: string;
  fecha_fin?: string;
  objetivo?: string;
  estado?: SprintStatus;
  capacidad_horas?: number;
  horas_planeadas?: number;
}

// Miembro
export interface ProyectoMiembro {
  proyecto_id: string;
  usuario_id: string;
  usuario: UsuarioBasico;
  rol: RolProyecto;
  fecha_asignacion: string;
}

export interface ProyectoMiembroCreate {
  usuario_id: string;
  rol?: RolProyecto;
}

export interface ProyectoMiembroUpdate {
  rol: RolProyecto;
}

// Resumen del proyecto
export interface ProyectoResumen {
  proyecto_id: string;
  codigo: string;
  nombre: string;
  estado: ProjectStatus;
  progreso_porcentaje: number;
  horas_estimadas?: number;
  horas_registradas: number;
  fases: {
    total: number;
    completadas: number;
    en_progreso: number;
  };
  sprints: {
    total: number;
    completados: number;
    sprint_activo?: string;
  };
  miembros_count: number;
  fecha_inicio_planeada?: string;
  fecha_fin_planeada?: string;
  fecha_inicio_real?: string;
  fecha_fin_real?: string;
}
